import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Comic, Language } from "../../types";
import { deleteComic, getImageUrl } from "../../api";
import { ISO_LANGUAGES } from "../../data/languages";
import { useSettings } from "../../contexts/SettingsContext";
import { useFieldVisibility } from "../../hooks/useFieldVisibility";
import { LazyImage } from "../ui/LazyImage";

interface ComicCardProps {
    comic: Comic;
    onComicUpdate: () => void;
    onEditClick: (comic: Comic) => void;
    readOnly?: boolean;
}

const ComicCard: React.FC<ComicCardProps> = ({
    comic,
    onComicUpdate,
    onEditClick,
    readOnly = false,
}) => {
    const { t } = useTranslation();
    const { settings } = useSettings();
    const { isFieldVisible } = useFieldVisibility();
    const [isDeleting, setIsDeleting] = useState(false);

    // Get showAdultContent from settings context, fallback to localStorage for unauthenticated users
    const showAdultContent = settings?.show_adult_content ?? (localStorage.getItem("showAdultContent") === "true");

    const getLanguageName = (language: Language) => {
        // First try to get translation using the language code
        if (language.code) {
            const translationKey = `languages.${language.code}`;
            const translated = t(translationKey);
            if (translated !== translationKey) {
                return translated;
            }
        }
        
        // If no translation found, try to find in ISO list for fallback
        const isoLang = ISO_LANGUAGES.find(iso => iso.code === language.code);
        if (isoLang) {
            // Fallback to native name or English name
            return isoLang.nativeName || isoLang.name;
        }
        
        // Final fallback to database name
        return language.name;
    };


    const handleDelete = async () => {
        if (window.confirm(t('common.areYouSureDeleteComic'))) {
            setIsDeleting(true);
            try {
                await deleteComic(comic.id);
                onComicUpdate();
            } catch (error) {
                
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "owned":
                return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200";
            case "wishlist":
                return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200";
            case "ordered":
                return "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200";
            case "loaned":
                return "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200";
            default:
                return "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200";
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow group relative">
            {isFieldVisible('cover_image') && (
                <div className="relative pt-4 px-4">
                    {comic.cover_image ? (
                        <LazyImage
                            src={getImageUrl(comic.cover_image)}
                            alt={comic.title}
                            className="w-full h-64 object-contain"
                            skeletonClassName="w-full h-64"
                            fallbackSrc=""
                        />
                    ) : (
                        <div className="w-full h-64 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <span className="text-gray-500 dark:text-gray-400">
                                {t('common.noCover')}
                            </span>
                        </div>
                    )}
                    {showAdultContent && comic.is_adult_content && isFieldVisible('is_adult_content') && (
                        <div className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded text-xs font-medium">
                            {t('list.adult')}
                        </div>
                    )}
                </div>
            )}

            <div className="p-4">
                {comic.series && isFieldVisible('series') && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1 font-medium">
                        {comic.series}{comic.issue_number && isFieldVisible('issue_number') ? ` ${comic.issue_number}` : ''}
                    </p>
                )}
                <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-lg leading-tight text-gray-900 dark:text-gray-100">
                        {comic.title}
                    </h3>
                    {isFieldVisible('status') && (
                        <span
                            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(
                                comic.status
                            )}`}
                        >
                            {t(`status.${comic.status}`)}
                        </span>
                    )}
                </div>


                {comic.language && isFieldVisible('language_id') && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                        {t("common.language")}: {getLanguageName(comic.language)}
                    </p>
                )}

                {comic.grade && isFieldVisible('grade') && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                        {t("comic.grade")}: {comic.grade.toFixed(1)}
                    </p>
                )}

                {comic.status === "loaned" && comic.loaned_to && isFieldVisible('loaned_to') && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mb-2">
                        {t("comic.loanedTo")}: {comic.loaned_to}
                    </p>
                )}

                {comic.people.length > 0 && isFieldVisible('people') && (
                    <div className="mb-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {t("comic.people")}:
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {comic.people.slice(0, 3).map((person) => (
                                <span
                                    key={person.id}
                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded"
                                >
                                    {person.name}
                                </span>
                            ))}
                            {comic.people.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded">
                                    {t('common.moreItems', { count: comic.people.length - 3 })}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {comic.tags.length > 0 && isFieldVisible('tags') && (
                    <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {t("comic.tags")}:
                        </p>
                        <div className="flex flex-wrap gap-1">
                            {comic.tags.slice(0, 3).map((tag) => (
                                <span
                                    key={tag.id}
                                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded"
                                >
                                    {tag.name}
                                </span>
                            ))}
                            {comic.tags.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-xs rounded">
                                    {t('common.moreItems', { count: comic.tags.length - 3 })}
                                </span>
                            )}
                        </div>
                    </div>
                )}


            </div>
            
            {!readOnly && (
                <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-70 transition-opacity">
                    <button
                        onClick={() => onEditClick(comic)}
                        className="p-1.5 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-white dark:hover:bg-gray-700 transition-colors shadow-md"
                        title={t('tooltip.editComic')}
                    >
                        <PencilIcon className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="p-1.5 bg-white/90 dark:bg-gray-800/90 rounded-full hover:bg-red-50 dark:hover:bg-red-900/50 transition-colors shadow-md disabled:opacity-50"
                        title={isDeleting ? t('common.deleting') : t('tooltip.deleteComic')}
                    >
                        <TrashIcon className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ComicCard;
